import React, { useState } from "react";
import { Button, Input, Alert, Form } from "reactstrap";
import { useAuth0 } from "@auth0/auth0-react";
import { readOffersNear } from "../utils/apiService";
import { currentPosition, directionsUrl, geocodeAddress } from "../utils/maps";

export const RADII = [
  { meters: 1000, label: "1 km" },
  { meters: 5000, label: "5 km" },
  { meters: 10000, label: "10 km" },
  { meters: 25000, label: "25 km" },
];

export const formatDistance = (meters) =>
  meters < 1000 ? `${Math.max(10, Math.round(meters / 10) * 10)} m` : `${(meters / 1000).toFixed(1)} km`;

/** What a locked deal asks for, e.g. "Visit 3 times in 7 days to unlock". */
export const unlockHint = ({ minVisits, windowDays }) => {
  if (!minVisits) return "Visit to unlock";
  const times = minVisits === 1 ? "once" : `${minVisits} times`;
  return windowDays ? `Visit ${times} in ${windowDays} days to unlock` : `Visit ${times} to unlock`;
};

/**
 * Deals near you: take the browser's position (or a searched address),
 * then list every active deal within the chosen radius, nearest first, locked or unlocked.
 */
const Deals = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [radius, setRadius] = useState(5000);
  const [address, setAddress] = useState("");
  const [where, setWhere] = useState(null); // { latitude, longitude, label }
  const [offers, setOffers] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const search = async (point, radiusInMeters = radius) => {
    setBusy("Finding deals…");
    setError(null);
    try {
      let token = null;
      try {
        token = await getAccessTokenSilently();
      } catch (e) {
        // Signed out still works: deals with a visit rule then show as locked.
      }
      const data = await readOffersNear({ latitude: point.latitude, longitude: point.longitude, radiusInMeters }, token);
      setWhere(point);
      setOffers(data.offers || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const useMyLocation = async () => {
    setBusy("Finding your location…");
    setError(null);
    try {
      const pos = await currentPosition();
      await search({ ...pos, label: "your location" });
    } catch (e) {
      setError(e.message);
      setBusy(null);
    }
  };

  const searchAddress = async (e) => {
    e.preventDefault();
    if (!address.trim()) return;
    setBusy("Finding the address…");
    setError(null);
    try {
      const found = await geocodeAddress(address.trim());
      await search(found);
    } catch (err) {
      setError(err.message);
      setBusy(null);
    }
  };

  const changeRadius = (value) => {
    const meters = Number(value);
    setRadius(meters);
    if (where) search(where, meters);
  };

  const radiusLabel = (RADII.find((r) => r.meters === radius) || {}).label;

  return (
    <div className="mb-5">
      <div className="page-header">
        <div>
          <h1 className="page-title">Deals near you</h1>
          <p className="page-subtitle">Offers from local businesses around you. Some unlock after a few visits.</p>
        </div>
      </div>

      <div className="panel mb-4">
        <div className="panel-body">
          <div className="d-flex flex-wrap align-items-center" style={{ gap: 12 }}>
            <Button color="primary" onClick={useMyLocation} disabled={!!busy}>
              Use my location
            </Button>
            <Form onSubmit={searchAddress} className="d-flex flex-grow-1" style={{ gap: 8, minWidth: 240 }}>
                <Input
                  type="search"
                  placeholder="or search a town, street or postcode"
                  aria-label="Address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  disabled={!!busy}
                />
                <Button color="light" type="submit" disabled={!!busy || !address.trim()}>Search</Button>
              </Form>
            <div className="d-flex align-items-center ml-auto" style={{ gap: 8 }}>
              <label htmlFor="dealRadius" className="mb-0">Within</label>
              <Input
                id="dealRadius"
                type="select"
                value={radius}
                onChange={(e) => changeRadius(e.target.value)}
                disabled={!!busy}
                style={{ width: 100 }}
              >
                {RADII.map((r) => <option key={r.meters} value={r.meters}>{r.label}</option>)}
              </Input>
            </div>
          </div>
        </div>
      </div>

      {error && <Alert color="danger">{error}</Alert>}
      {busy && <div className="loading-state">{busy}</div>}

      {!busy && offers === null && !error && (
        <div className="empty-state">
          <h5>Where are you?</h5>
          <p className="mb-0">Share your location to see the deals around you.</p>
        </div>
      )}

      {!busy && offers !== null && (
        <div className="panel" data-testid="deals">
          <div className="panel-header">
            <div className="panel-title">
              {offers.length === 1 ? "1 deal" : `${offers.length} deals`} within {radiusLabel} of {where.label || "the address"}
            </div>
          </div>
          {offers.length === 0 ? (
            <div className="empty-state">
              <h5>No deals here yet</h5>
              <p className="mb-0">Try a wider radius, or check back soon.</p>
            </div>
          ) : (
            <ul className="deal-list">
              {offers.map((o) => (
                <li key={o.serviceFulfilmentConfigId} className="deal">
                  <div className="deal-main">
                    <div className="cell-strong">{o.offerName}</div>
                    <div className="cell-muted">
                      {o.orgName}
                      {o.serviceName ? ` · ${o.serviceName}` : ""}
                    </div>
                    {o.unlocked
                      ? <span className="tag btn-pill-green mt-2">Unlocked</span>
                      : <span className="tag tag-grey mt-2">{unlockHint(o)}</span>}
                  </div>
                  <div className="deal-side">
                    <div className="cell-faint">{formatDistance(o.distanceMeters)}</div>
                    <a className="btn-pill btn-pill-blue" href={directionsUrl(o.latitude, o.longitude)} target="_blank" rel="noopener noreferrer">
                      Directions
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default Deals;
